import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
    BellIcon,
    ChangeRetentionPeriodIcon,
    DeleteIcon,
    DownloadIcon,
    PlusCircleIcon,
} from "src/assets/images/svg/IconCollection";
import { Option } from "react-dropdown";
import _ from "lodash";

import { DocumentToolbarConstants, MessageToolbarConstants, NewDRConstants } from "src/Core/Common/Constants";
import { DetailedStatus, FolderType } from "src/Core/Common/Enums";
import {
    IDocumentRequestDetailedModel,
    IDocumentRequestFile,
    IDocumentRequestModel,
    IDocumentRequestMoreModel,
    initialDocumentRequestMoreModel,
    initialNewDocumentState,
} from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import {
    getDateForDocumentExpiryView,
    getFormattedDueDate,
    getFormattedSentDateForDetailedView,
    getYearFromDate,
} from "src/helper/DateHelperFunctions";
import { convertTextToHtml, enableEditPermission, getFormattedLinkedRecipients, getHeightForDocumentBody, isDelegatedUser } from "src/helper/HelperFunctions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import {
    deleteAllUploadedFiles,
    detailDeleteDocumentRequest,
    downloadAllUploadedDocument,
    downloadUploadedFile,
    requestNewDocument,
    sendSpecificFileReminder,
    updateDocumentStatus,
    previewUploadedDocument,
    deleteUploadedFile,
    downloadUploadedFiles,
} from "../../actionCreators/documentRequestActionCreators";
import "src/assets/style/site.css";
import { DocumentRow } from "./DocumentRow";
import { ChangeRetentionPeriodModal } from "../Common/Modal/ChangeRetentionPeriodModal";
import { ColorConstants } from "src/assets/custom/colors";
import { IDocumentRequestTemplateFile } from "src/Core/ViewModels/Template/TemplateModel";
import { DocumentRequestMore } from "./DocumentRequestMore";
import CustomToggleSwitch from "src/components/Common/ui/Switch/CustomToggleSwitch";
import { ApplicationState } from "src/store";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";
import { fetchRequestNotificationsSettings } from "src/actionCreators/adminSettingsActionCreators";
import { PrintComponent } from "../Common/PrintComponent/PrintComponent";
import { DocumentRequestPrint } from "./DocumentRequestPrint";
import { CustomModal } from "../Common/Modal/CustomModal";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";

interface IDocumentRequestDetailedProps {
    documentDetail: IDocumentRequestDetailedModel;
    isArchive?: boolean;
    folder?: IFolderModel;
    resetSelection: (documentStatus?: DetailedStatus, documentRequestId?: number) => void;
    resetDocumentRequestSelection: () => void;
    detailedActionSuccessCallback: (documentId: number) => void;
    handleDocumentResendAccessLink: (documentRequestIds: number[], isListingAction?: boolean) => void;
    hideDocumentDetail?: (hide: boolean) => void;
    handleDocumentSendReminder: (documentRequestIds: number[], isListingAction: boolean, resourceId: string) => void;
    retentionOptions: Option[];
    handleGetDocumentDetail: (documentRequestId: number) => void;
}

export const DocumentRequestDetailed: React.FC<IDocumentRequestDetailedProps> = ({
    documentDetail,
    isArchive,
    folder,
    resetSelection,
    resetDocumentRequestSelection,
    handleDocumentSendReminder,
    detailedActionSuccessCallback,
    hideDocumentDetail,
    handleGetDocumentDetail,
    retentionOptions,
}) => {
    const dispatch = useDispatch();
    const { recipients, documentRequest:document } = documentDetail;
    const [showDeleteRequest, toggleShowDeleteRequest] = useState(false);
    const [showDeleteDocument, toggleShowDeleteDocument] = useState(false);
    const [showChangeStatus, toggleChangeStatus] = useState(false);
    const [showStatusDropdown, toggleStatusDropdown] = useState(false);
    const [documentRequestFileId, setDocumentRequestFileId] = useState(0);
    const [showChangeRetentionModal, setShowChangeRetentionModal] = useState(false);
    const [showRequestMore, toggleRequestMore] = useState(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [filesData, setFilesData] = useState<IDocumentRequestTemplateFile[]>(initialNewDocumentState.requestedFiles);
    const [isRequestMore, setIsRequestMore] = useState<boolean>(false);
    const [documentRequestMore, setDocumentRequestMore] = useState<IDocumentRequestMoreModel>(
        initialDocumentRequestMoreModel
    );
    const { requestNotificationSettings } = useSelector((appState: ApplicationState) => appState.adminSettingsState);
    const [notifyOnCompletion, setNotifyOnCompletion] = useState(true);
    const [activeDocuments, setActiveDocuments] = useState<number[]>([]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [collapsibleItems, setCollapsibleItems] = useState<number[]>([]);
    const [uploadedFilesCount, setUploadedFilesCount] = useState<number>(0);
    const accordionRef = React.useRef<any>(null);
    const userSettingsStore = useSelector((state: ApplicationState) => state.userSettingsState);
    accordionRef.current = activeDocuments;
    const mailboxData = useSelector((state: ApplicationState) => state.commonState.mailboxUserData);
    const user = useSelector((state: ApplicationState) => state.userProfile);
    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );

    useEffect(() => {
        toggleStatusDropdown(false);
        setIsExpanded(false);
        setActiveDocuments([]);
        const collapsibleItems: number[] = [];
        let totalUploadedFiles = 0;
        document?.requestedFiles?.forEach((file: IDocumentRequestFile) => {
            if (file.uploadedFiles.length > 0) {
                collapsibleItems.push(file.documentRequestFileId);
                totalUploadedFiles += file.uploadedFiles.length;
            }
        });
        setUploadedFilesCount(totalUploadedFiles);
        setCollapsibleItems(collapsibleItems);
    }, [document]);

    const onClickDeleteDocument = (fileId: number, isFileLevelOperation?: boolean) => {
        setDocumentRequestFileId(fileId);
        if (isFileLevelOperation) {
            deleteUploadedDocumentSubmit(isFileLevelOperation, fileId);
            return;
        }
        toggleShowDeleteDocument(true);
    };

    useEffect(() => {
        if (isRequestMore && !validated) {
            dispatch(
                requestNewDocument(documentRequestMore, ResourceIdLocators.DRL.DRLDtlBtnRequestMore, () => {
                    setIsRequestMore(false);
                    toggleRequestMore(false);
                    handleGetDocumentDetail(documentRequestMore.documentRequestId);
                })
            );
        }
    }, [documentRequestMore]);

    useEffect(() => {
        dispatch(fetchRequestNotificationsSettings());
        dispatch(fetchUserSettings());
    }, []);

    const deleteUploadedDocumentSubmit = (isFileLevelOperation?: boolean, fileId?: number) => {
        if (isFileLevelOperation && fileId) {
            dispatch(
                deleteUploadedFile(
                    fileId,
                    document.documentRequestId,
                    ResourceIdLocators.DRL.DRLBtnDeleteDocument,
                    deleteUploadedDocumentCallback
                )
            );
        } else {
            dispatch(
                deleteAllUploadedFiles(
                    document.documentRequestId,
                    documentRequestFileId,
                    ResourceIdLocators.DRL.DRLBtnDeleteDocument,
                    deleteUploadedDocumentCallback
                )
            );
        }
    };

    useEffect(() => {
        const notify = !requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification
            ? requestNotificationSettings?.isRequestCompletionNotificationEnabled
            : userSettingsStore?.userSettings?.documentRequestNotificationsSettings
                ?.isDocumentRequestCompletionNotificationEnabled;
        setNotifyOnCompletion(notify);
    }, [requestNotificationSettings, userSettingsStore?.userSettings?.documentRequestNotificationsSettings]);

    const deleteDocumentRequestSubmit = () => {
        dispatch(
            detailDeleteDocumentRequest(
                document.documentRequestId,
                isArchive ? ResourceIdLocators.MyFiles.MyFilesBtnDRLDetailDeleteRequest : ResourceIdLocators.DRL.DRLDtlBtnDeleteRequest,
                FolderType.DOCUMENTREQUEST,
                successDeleteCallbackAction
            )
        );
    };

    const handleDocumentStatusChange = () => {
        toggleChangeStatus(true);
    };

    const handleChangeDocumentStatusSubmit = () => {
        dispatch(
            updateDocumentStatus(
                document.documentRequestId,
                document.status === DetailedStatus.OPEN || document.status === DetailedStatus.REOPEN
                    ? DetailedStatus.COMPLETED
                    : DetailedStatus.REOPEN,
                ResourceIdLocators.DRL.DRLBtnChangeState,
                document.status === DetailedStatus.COMPLETED ? notifyOnCompletion : false,
                successCallbackAction
            )
        );
    };

    const deleteUploadedDocumentCallback = () => {
        toggleShowDeleteDocument(false);
        setDocumentRequestFileId(0);
        detailedActionSuccessCallback(document.documentRequestId);
    };

    const successCallbackAction = () => {
        toggleShowDeleteRequest(false);
        toggleChangeStatus(false);
        resetSelection(document.status, document.documentRequestId);
    };

    const successDeleteCallbackAction = () => {
        toggleShowDeleteRequest(false);
        toggleChangeStatus(false);
        resetDocumentRequestSelection();
    };

    const onClickDownloadFile = (fileName: string, fileGuid: string) => {
        dispatch(
            downloadUploadedFile(
                fileName,
                fileGuid,
                getYearFromDate(document.sentDate),
                ResourceIdLocators.DRL.DRLBtnDownloadFile,
                openSaasLinkInNewTab
            )
        );
    };

    const onClickDownloadAllFiles = (file: IDocumentRequestFile) => {
        dispatch(
            downloadUploadedFiles(
                file,
                getYearFromDate(document.sentDate),
                ResourceIdLocators.DRL.DRLBtnDownloadFiles,
                openSaasLinkInNewTab
            )
        );
    };

    const onClickDownloadAllDocuments = () => {
        if (document.requestedFiles) {
            dispatch(
                downloadAllUploadedDocument(
                    document.requestedFiles,
                    getYearFromDate(document.sentDate),
                    ResourceIdLocators.DRL.DRLBtnDownloadFiles,
                    openSaasLinkInNewTab
                )
            );
        }
    };

    const openSaasLinkInNewTab = (link: string) => {
        window.open(link, "_blank");
    };

    const onClickDocumentSendReminder = () => {
        handleDocumentSendReminder([document.documentRequestId], false, ResourceIdLocators.DRL.DRLDtlBtnSendReminder);
    };

    const onClickSpecificFileSendReminder = (fileName: string) => {
        dispatch(
            sendSpecificFileReminder(
                document.documentRequestId,
                fileName,
                ResourceIdLocators.DRL.DRLDtlBtnDocSendReminder
            )
        );
    };

    const showDownloadAll = (): boolean => {
        let isDisabled = true;
        if (document.requestedFiles) {
            document.requestedFiles.forEach((file: IDocumentRequestFile) => {
                if (file.uploadedFiles && file.uploadedFiles.length > 0) {
                    isDisabled = false;
                }
            });
        }
        return isDisabled;
    };

    const getDropdownToogleLabel = () => {
        switch (document.status) {
            case DetailedStatus.OPEN:
                return DocumentToolbarConstants.OPEN_STATUS;
            case DetailedStatus.REOPEN:
                return DocumentToolbarConstants.OPEN_LABEL;
            case DetailedStatus.COMPLETED:
                return DocumentToolbarConstants.COMPLETED_LABEL;
        }
    };

    const onClickPreviewUploadedDocument = (
        fileName: string,
        fileGuid: string,
        fileType: string,
        callback?: (
            fileName: string,
            fileGuid: string,
            fileType: string,
            requestedYear: number,
            resourceId: string
        ) => void
    ) => {
        dispatch(previewUploadedDocument(fileName, fileGuid, getYearFromDate(document.sentDate), fileType));
        if (callback) {
            callback(
                fileName,
                fileGuid,
                fileType,
                getYearFromDate(document.sentDate),
                ResourceIdLocators.DRL.DRLBtnDownloadFile
            );
        }
    };

    const onClickAddMore = () => {
        const documentRequestFile: IDocumentRequestFile[] = [];
        setValidated(false);
        filesData.forEach(x => {
            const tempData: any = {};

            if (x.name.trim() == "") {
                setValidated(true);
            }

            tempData.name = x.name;
            tempData.description = x.description;
            tempData.documentCategory = x.documentCategory;
            tempData.documentRequestId = document.documentRequestId;
            documentRequestFile.push(tempData);
        });

        setIsRequestMore(true);

        setDocumentRequestMore({
            documentRequestModel: documentRequestFile,
            documentRequestId: document.documentRequestId,
            subject: document.subject,
            dueDate: document.dueDate,
            recipientEmail: document.recipient?.email,
            documentRequestGuid: document.documentRequestGuid,
        });
    };

    const handleFilesFormChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { id, value } = e.target;

        const filesDataCopy: IDocumentRequestTemplateFile[] = _.cloneDeep(filesData);
        filesDataCopy[index][id as keyof IDocumentRequestTemplateFile] = value as any;
        setFilesData(filesDataCopy);
        setValidated(false);
    };

    const handleCategoryChange = (value: any, index: number) => {
        const filesDataCopy: IDocumentRequestTemplateFile[] = _.cloneDeep(filesData);
        filesDataCopy[index].documentCategory = value as any;
        setFilesData(filesDataCopy);
    };

    const onRequestMoreModalHide = () => {
        setValidated(false);
        toggleRequestMore(false);
        setDocumentRequestMore(initialDocumentRequestMoreModel);
        setFilesData(initialNewDocumentState.requestedFiles);
    };

    const onClickRemoveRow = (index: number) => {
        const filesDataCopy = [...filesData];
        filesDataCopy.splice(index, 1);
        setFilesData(filesDataCopy);
    };

    const onClickAddFiles = () => {
        const filesDataCopy = [...filesData];
        filesDataCopy.push({
            name: "",
            description: "",
        });
        setFilesData(filesDataCopy);
    };

    const NotifyCompletion = (
        <div className="notify-container">
            <CustomToggleSwitch
                label={NewDRConstants.NOTIFY_ON_COMPLETION}
                handleChange={() => {
                    setNotifyOnCompletion(value => !value);
                }}
                switched={
                    !requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification
                        ? requestNotificationSettings?.isRequestCompletionNotificationEnabled
                        : notifyOnCompletion
                }
                disabled={!requestNotificationSettings?.isUserAllowedToChangeRequestCompletionNotification}
            />
        </div>
    );

    const onRequestMoreButtonClick = () => {
        setFilesData(initialNewDocumentState.requestedFiles);
        toggleRequestMore(true);
    };

    const onHideChangeStatusModal = () => {
        toggleChangeStatus(false);
    };

    const onAccordionClick = (documentRequestId: number) => {
        if (activeDocuments.includes(documentRequestId)) {
            const index = activeDocuments.indexOf(documentRequestId);
            if (index > -1) {
                activeDocuments.splice(index, 1);
            }
        } else {
            activeDocuments.push(documentRequestId);
        }
        if (activeDocuments.length === collapsibleItems.length) {
            setIsExpanded(true);
        }
        if (activeDocuments.length === 0) {
            setIsExpanded(false);
        }
        setActiveDocuments(activeDocuments);
    };

    const hasUploadedFiles = (document: IDocumentRequestModel): boolean => {
        return document.requestedFiles &&
            document.requestedFiles.length > 0 &&
            document.requestedFiles.some(x => x.uploadedFiles && x.uploadedFiles.length > 0)
            ? true
            : false;
    };

    const onExpandAllClick = () => {
        if (hasUploadedFiles(document)) {
            if (activeDocuments.length > 0 && isExpanded) {
                setActiveDocuments([]);
                setIsExpanded(false);
            } else {
                const documentRequestIds: number[] = [];
                document &&
                    document?.requestedFiles?.forEach((file: IDocumentRequestFile) => {
                        if (file.uploadedFiles.length > 0) {
                            documentRequestIds.push(file.documentRequestFileId);
                        }
                    });
                setActiveDocuments(documentRequestIds);
                setIsExpanded(true);
            }
        }
    };

    let isExpandClassName = isDelegatedUser() ? "bottom-padding" : "";
    isExpandClassName = !hasUploadedFiles(document) ? `disabled ${isExpandClassName}` : isExpandClassName;

    return (
        <div className="message-pane document-pane document-request-detailed">
            <div className="message-pane-header flex-row-space-between">
                <div className="flex-row align-items-center left-content">
                    <span className="back-btn" title="Back" onClick={() => hideDocumentDetail(true)}>
                        <span className="fas fa-arrow-left"></span>
                    </span>
                </div>
                <div className="flex-row header-actions">
                    <ButtonGroup className="message-pane-action" aria-label="Message pane actions">
                        {
                            <Button variant="link" className="btn-only" title="Print Request">
                                <PrintComponent
                                    showPrintIcon
                                    spamMessage={true}
                                    resourceId={ResourceIdLocators.Inbox.MsgDtlBtnPrint}
                                >
                                    <DocumentRequestPrint
                                        document={document}
                                        uploadedFilesCount={uploadedFilesCount}
                                        titleText={DocumentToolbarConstants.REQUEST_DOCUMENT_PRINT_TITLE_TEXT}
                                        recipients={recipients.filter(r => !r.isPrimaryRecipient)}
                                    />
                                </PrintComponent>
                            </Button>
                        }
                        {!isDelegatedUser() && (
                            <Button
                                variant="link"
                                className={`btn-only ${
                                    enableEditPermission(user, mailboxData, folder) ? "" : "toolbar-icon-disabled"
                                }`}
                                title={DocumentToolbarConstants.TITLE_DELETE}
                                data-resource-id={
                                    isArchive
                                        ? ResourceIdLocators.MyFiles.MyFilesBtnDRLDetailDeleteRequest
                                        : ResourceIdLocators.DRL.DRLDtlBtnDeleteRequest
                                }
                                onClick={() => toggleShowDeleteRequest(true)}
                                disabled={!enableEditPermission(user, mailboxData, folder)}
                            >
                                <DeleteIcon height={16} width={16} />
                            </Button>
                        )}
                        {!isDelegatedUser() && !isArchive && (
                            <Button
                                variant="link"
                                className="btn-only"
                                disabled={
                                    !(document.status !== DetailedStatus.COMPLETED) ||
                                    !messageOptionsSettings?.retentionSettings
                                        ?.isUserAllowedToChangeMessageAndDRRetentionPeriod
                                }
                                title={DocumentToolbarConstants.TITLE_CHANGE_RETENTION}
                                data-resource-id={ResourceIdLocators.DRL.DRLBtnChangeExpiry}
                                onClick={() => setShowChangeRetentionModal(true)}
                            >
                                <ChangeRetentionPeriodIcon height={16} width={16} />
                            </Button>
                        )}

                        {!isArchive && (
                            <Button
                                variant="link"
                                className="btn-only"
                                disabled={!(document.status !== DetailedStatus.COMPLETED)}
                                title={DocumentToolbarConstants.TITLE_SEND_REMINDER}
                                data-resource-id={ResourceIdLocators.DRL.DRLDtlBtnSendReminder}
                                onClick={onClickDocumentSendReminder}
                            >
                                <BellIcon height={16} width={16} />
                            </Button>
                        )}

                        {!showDownloadAll() && (
                            <Button
                                data-resource-id={ResourceIdLocators.DRL.DRLBtnDownloadFiles}
                                onClick={onClickDownloadAllDocuments}
                                variant="link"
                                className="btn-only"
                                title={DocumentToolbarConstants.TITLE_DOWNLOAD}
                            >
                                <DownloadIcon height={16} width={16} />
                            </Button>
                        )}
                    </ButtonGroup>
                </div>
            </div>
            <div className="document-pane-header">
                <div className="py-2">
                    <div className="fs-5 ellipsis" title={document.subject}>
                        {document.subject}
                    </div>
                    <p className="mt-3">
                        <b>To:</b> {document.recipient?.email}
                    </p>

                    {recipients.filter(r => !r.isPrimaryRecipient).length > 0 && (
                        <p className="mt-3">
                            {getFormattedLinkedRecipients(
                                recipients.filter(r => !r.isPrimaryRecipient).map(x => x.email)
                            )}
                        </p>
                    )}

                    <p className="mt-3">
                        <b>Sent On:</b>
                        <span> {getFormattedSentDateForDetailedView(document.sentDate)}</span>
                    </p>

                    {(document.status === DetailedStatus.OPEN || document.status === DetailedStatus.REOPEN) &&
                        document.dueDate && (
                            <p className="mt-3">
                                <b>Due On:</b>
                                <span> {getFormattedDueDate(document.dueDate)}</span>
                            </p>
                        )}
                    {document.status === DetailedStatus.COMPLETED && (
                        <p className="mt-3">
                            <b>Expires On:</b>
                            <span> {getDateForDocumentExpiryView(document.updatedDate, document.expiryDays)}</span>
                        </p>
                    )}
                    <p className="mt-3">
                        <b>Tax Year:</b>
                        <span> {document.year} </span>
                    </p>
                </div>
                {!isArchive && (
                    <div className="status-dropdown-wrapper d-flex align-items-center">
                        <p className="me-3">
                            <b>Status:</b>
                        </p>
                        <div data-resource-id={ResourceIdLocators.DRL.DRLBtnChangeState}>
                            <Dropdown
                                className={`secondary-dropdown document-status-dropdown ${
                                    isDelegatedUser() ? "toolbar-icon-disabled" : ""
                                }`}
                                show={showStatusDropdown}
                                onToggle={() => toggleStatusDropdown(!showStatusDropdown)}
                            >
                                <Dropdown.Toggle className={"Dropdown-control"} disabled={isDelegatedUser()}>
                                    {getDropdownToogleLabel()}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="Dropdown-menu">
                                    <Dropdown.Item className="Dropdown-option" onClick={handleDocumentStatusChange}>
                                        {document.status !== DetailedStatus.COMPLETED
                                            ? DocumentToolbarConstants.COMPLETED_STATUS
                                            : DocumentToolbarConstants.REOPEN_STATUS}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                )}
            </div>

            <hr className="mb-0" />
            <div
                style={{
                    height: getHeightForDocumentBody(
                        (document.status === DetailedStatus.OPEN && Boolean(document.dueDate)) ||
                            document.status === DetailedStatus.COMPLETED,
                        document.subject
                    ),
                }}
                className="document-body-wrapper"
            >
                {document.message && (
                    <div
                        className="client-note"
                        dangerouslySetInnerHTML={{
                            __html: convertTextToHtml(document.message),
                        }}
                    ></div>
                )}
                <div
                    className={`document-row-list-wrapper ${
                        document.message?.length > 50 ? "doc-max-height-big-subject" : "doc-max-height-small-subject"
                    }`}
                >
                    {document.requestedFiles &&
                        document.requestedFiles.length > 0 &&
                        document.requestedFiles.map(file => {
                            return (
                                <DocumentRow
                                    key={file.documentRequestFileId}
                                    id={file.documentRequestFileId}
                                    file={file}
                                    activeDocuments={accordionRef.current}
                                    isExpanded={isExpanded}
                                    isArchive={isArchive}
                                    setActiveDocument={onAccordionClick}
                                    onClickDeleteDocument={onClickDeleteDocument}
                                    onClickDownloadFile={onClickDownloadFile}
                                    onClickDownloadAllFiles={onClickDownloadAllFiles}
                                    onClickSpecificFileSendReminder={onClickSpecificFileSendReminder}
                                    onClickPreviewUploadedDocument={onClickPreviewUploadedDocument}
                                    status={document.status}
                                />
                            );
                        })}
                </div>
                <div className="document-pane-footer">
                    {!isDelegatedUser() && !isArchive && (
                        <button
                            tabIndex={0}
                            disabled={document.status === DetailedStatus.COMPLETED}
                            title={
                                document.status === DetailedStatus.COMPLETED &&
                                NewDRConstants.REQUEST_MORE_DISABLED_TITLE
                            }
                            className={"button-primary-blue-2 request-more-btn"}
                            data-resource-id={ResourceIdLocators.DRL.DRLDtlBtnRequestMore}
                            onClick={onRequestMoreButtonClick}
                        >
                            <PlusCircleIcon color={ColorConstants.white} />
                            {NewDRConstants.REQUEST_MORE_TEXT}
                        </button>
                    )}
                    <span className={isExpandClassName} onClick={onExpandAllClick}>
                        {activeDocuments.length > 0 ? "Collapse All" : "Expand All"}
                    </span>
                </div>
                <CustomModal
                    className="request-more-modal"
                    show={showRequestMore}
                    title={NewDRConstants.REQUEST_MORE_TEXT}
                    confirmButtonName={NewDRConstants.OK_BUTTON_TEXT}
                    onSubmit={onClickAddMore}
                    onHide={() => onRequestMoreModalHide()}
                    cancelButtonName="Cancel"
                >
                    <DocumentRequestMore
                        documentRequestId={document.documentRequestId}
                        filesData={filesData}
                        validated={validated}
                        onClickAddFiles={onClickAddFiles}
                        onClickRemoveRow={onClickRemoveRow}
                        onFilesFormChange={handleFilesFormChange}
                        onCategoryChange={handleCategoryChange}
                    />
                </CustomModal>
                <CustomModal
                    className="delete-message-modal"
                    show={showDeleteRequest}
                    title={DocumentToolbarConstants.DELETE_REQUEST}
                    cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                    confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                    onSubmit={deleteDocumentRequestSubmit}
                    onHide={() => toggleShowDeleteRequest(false)}
                    isConfirmModal={true}
                >
                    <span>{DocumentToolbarConstants.DETAILED_DELETE_REQUEST}</span>
                </CustomModal>
                <CustomModal
                    className="change-dr-status-modal"
                    show={showChangeStatus}
                    title={DocumentToolbarConstants.TITLE_CHANGE_STATUS}
                    onHide={onHideChangeStatusModal}
                    additionalFooterElement={document.status === DetailedStatus.COMPLETED ? NotifyCompletion : ""}
                    onSubmit={handleChangeDocumentStatusSubmit}
                    confirmButtonName="Yes"
                    cancelButtonName="No"
                    isConfirmModal={true}
                >
                    <span>
                        {document.status === DetailedStatus.OPEN || document.status === DetailedStatus.REOPEN
                            ? DocumentToolbarConstants.CHANGE_STATUS_TO_COMPLETE_MESSAGE
                            : DocumentToolbarConstants.CHANGE_STATUS_TO_RE_OPEN_MESSAGE}
                    </span>
                </CustomModal>
                <CustomModal
                    className="delete-message-modal"
                    show={showDeleteDocument}
                    cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                    confirmButtonName={MessageToolbarConstants.TITLE_OK}
                    title={DocumentToolbarConstants.DELETE_DOCUMENT}
                    onSubmit={deleteUploadedDocumentSubmit}
                    onHide={() => toggleShowDeleteDocument(false)}
                    isConfirmModal={true}
                >
                    <span>{DocumentToolbarConstants.DETAILED_DELETE_DOCUMENT}</span>
                </CustomModal>
            </div>

            {showChangeRetentionModal && (
                <ChangeRetentionPeriodModal
                    showState={showChangeRetentionModal}
                    retentionOptions={retentionOptions}
                    changeRtnResourceId={ResourceIdLocators.DRL.DRLBtnChangeExpiry}
                    onHide={() => setShowChangeRetentionModal(false)}
                    onSubmit={() => setShowChangeRetentionModal(false)}
                    document={document}
                />
            )}
        </div>
    );
};
